import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Modal from '../Modal';
import { useToast } from '../Toast';
import { formatDate } from '../../utils/formatDate';
import { AICallLogItem, AICallStatus, AICallType, BodyResponse } from '../../types/aiCallLog';
import { getRequestBody, getResponseBody } from '../../api/aiCallLog';
import AICallEpisodeLink from './AICallEpisodeLink';

interface Props {
  item: AICallLogItem | null;
  open: boolean;
  onClose: () => void;
}

function typeLabel(t: TFunction, kind: AICallType): string {
  switch (kind) {
    case 'llm':
      return t('aiCallLogs.types.llm', { defaultValue: 'LLM' });
    case 'image':
      return t('aiCallLogs.types.image', { defaultValue: '图片' });
    case 'video':
      return t('aiCallLogs.types.video', { defaultValue: '视频' });
    case 'voice':
      return t('aiCallLogs.types.voice', { defaultValue: '语音' });
    case 'external':
      return t('aiCallLogs.types.external', { defaultValue: 'API' });
    default:
      return kind;
  }
}

function statusLabel(t: TFunction, status: AICallStatus): string {
  switch (status) {
    case 'submitted':
      return t('aiCallLogs.statuses.submitted', { defaultValue: '已提交' });
    case 'success':
      return t('aiCallLogs.statuses.success', { defaultValue: '成功' });
    case 'failed':
      return t('aiCallLogs.statuses.failed', { defaultValue: '失败' });
    default:
      return status;
  }
}

function stepLabel(t: TFunction, step: string): string {
  if (step === 'analysis.director_mark_mapping') {
    return t('aiCallLogs.steps.directorMarkMapping', { defaultValue: '导演标注映射' });
  }
  return step;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  return fallback;
}

function formatBody(body: unknown, emptyLabel: string): string {
  if (body === null || body === undefined) return emptyLabel;
  if (typeof body === 'string') return body;
  try {
    return JSON.stringify(body, null, 2);
  } catch {
    return String(body);
  }
}

interface MetaItemProps {
  label: string;
  children: React.ReactNode;
}

function MetaItem({ label, children }: MetaItemProps) {
  return (
    <div className="ai-call-detail-meta-item">
      <div className="ai-call-detail-meta-label">{label}</div>
      <div className="ai-call-detail-meta-value">{children}</div>
    </div>
  );
}

export default function AICallDrawer({ item, open, onClose }: Props) {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [req, setReq] = useState<BodyResponse | null>(null);
  const [resp, setResp] = useState<BodyResponse | null>(null);
  const [loading, setLoading] = useState<'req' | 'resp' | null>(null);

  useEffect(() => {
    if (!open) {
      setReq(null);
      setResp(null);
      setLoading(null);
    }
  }, [open, item?.id]);

  if (!item) return null;

  const loadingLabel = t('aiCallLogs.actions.loading', { defaultValue: '加载中…' });
  const emptyBodyLabel = t('aiCallLogs.drawer.bodyEmpty', { defaultValue: '(空)' });

  const loadReq = async (): Promise<void> => {
    setLoading('req');
    try {
      setReq(await getRequestBody(item.id));
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, loadingLabel));
    } finally {
      setLoading(null);
    }
  };

  const loadResp = async (): Promise<void> => {
    setLoading('resp');
    try {
      setResp(await getResponseBody(item.id));
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, loadingLabel));
    } finally {
      setLoading(null);
    }
  };

  const copyId = (): void => {
    if (!navigator.clipboard) {
      toast.error(
        t('aiCallLogs.drawer.copyUnsupported', { defaultValue: '当前浏览器不支持复制' }),
      );
      return;
    }
    navigator.clipboard
      .writeText(item.id)
      .then(() =>
        toast.success(t('aiCallLogs.drawer.copySuccess', { defaultValue: 'callID 已复制' })),
      )
      .catch(() =>
        toast.error(t('aiCallLogs.drawer.copyFailed', { defaultValue: '复制失败' })),
      );
  };

  const footer = (
    <>
      <button className="btn" onClick={copyId}>
        {t('aiCallLogs.drawer.copyId', { defaultValue: '复制 callID' })}
      </button>
      <button className="btn btn-primary" onClick={onClose}>
        {t('aiCallLogs.drawer.close', { defaultValue: '关闭' })}
      </button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="820px"
      title={t('aiCallLogs.drawer.title', {
        type: typeLabel(t, item.callType),
        platform: item.platform || '-',
        defaultValue: '调用详情 · {{type}} · {{platform}}',
      })}
      footer={footer}
    >
      <div className="ai-call-detail-meta">
        <MetaItem label={t('aiCallLogs.drawer.meta.callId', { defaultValue: 'Call ID' })}>
          <code>{item.id}</code>
        </MetaItem>
        <MetaItem label={t('aiCallLogs.drawer.meta.time', { defaultValue: '时间' })}>
          {formatDate(item.createdAt)}
        </MetaItem>
        <MetaItem label={t('aiCallLogs.drawer.meta.project', { defaultValue: '项目' })}>
          {item.projectId ? (
            <a href={`/projects/${item.projectId}`} target="_blank" rel="noopener noreferrer">
              <code>{item.projectId}</code>
            </a>
          ) : '-'}
        </MetaItem>
        <MetaItem label={t('aiCallLogs.drawer.meta.episode', { defaultValue: '分集' })}>
          <AICallEpisodeLink item={item} />
        </MetaItem>
        <MetaItem label={t('aiCallLogs.drawer.meta.operator', { defaultValue: '操作者' })}>
          {item.operatorId ? <code>{item.operatorId}</code> : '-'}
        </MetaItem>
        <MetaItem
          label={t('aiCallLogs.drawer.meta.platformModel', { defaultValue: '平台 / 模型' })}
        >
          {item.platform || '-'} {item.model && <span> / {item.model}</span>}
        </MetaItem>
        <MetaItem label={t('aiCallLogs.drawer.meta.step', { defaultValue: 'Step' })}>
          {item.step ? <code>{stepLabel(t, item.step)}</code> : '-'}
        </MetaItem>
        <MetaItem label={t('aiCallLogs.drawer.meta.status', { defaultValue: '状态' })}>
          <span className={`ai-call-badge status-${item.status}`}>
            {statusLabel(t, item.status)}
          </span>
          {item.httpStatus ? <span style={{ marginLeft: 8 }}>HTTP {item.httpStatus}</span> : null}
        </MetaItem>
        <MetaItem label={t('aiCallLogs.drawer.meta.duration', { defaultValue: '耗时' })}>
          {item.durationMs ? `${item.durationMs} ms` : '-'}
          {item.completedAt && (
            <span style={{ marginLeft: 8, color: 'var(--text-2)', fontSize: '0.78rem' }}>
              · {t('aiCallLogs.drawer.meta.completedAt', {
                time: formatDate(item.completedAt),
                defaultValue: '完成于 {{time}}',
              })}
            </span>
          )}
          {item.lastPolledAt && item.status === 'submitted' && (
            <span style={{ marginLeft: 8, color: 'var(--text-2)', fontSize: '0.78rem' }}>
              · {t('aiCallLogs.drawer.meta.heartbeat', {
                time: formatDate(item.lastPolledAt),
                defaultValue: '心跳 {{time}}',
              })}
            </span>
          )}
        </MetaItem>
        {item.truncated && (
          <MetaItem
            label={t('aiCallLogs.drawer.meta.truncatedLabel', { defaultValue: '提示' })}
          >
            <span style={{ color: '#b45309' }}>
              {t('aiCallLogs.drawer.meta.truncated', {
                defaultValue: 'Body 已截断（超过 5MB 上限）',
              })}
            </span>
          </MetaItem>
        )}
      </div>

      {item.errorMsg && (
        <div className="ai-call-detail-error">
          {t('aiCallLogs.drawer.error', {
            message: item.errorMsg,
            defaultValue: '错误：{{message}}',
          })}
        </div>
      )}

      <div className="ai-call-detail-section">
        <div className="ai-call-detail-section-header">
          <h4 className="ai-call-detail-section-title">
            {t('aiCallLogs.drawer.sections.request', { defaultValue: 'Request' })}
          </h4>
          {!req && (
            <button
              className="btn btn-secondary"
              onClick={loadReq}
              disabled={loading === 'req' || !item.hasRequestBody}
            >
              {loading === 'req'
                ? loadingLabel
                : item.hasRequestBody
                  ? t('aiCallLogs.drawer.viewRequestBody', { defaultValue: '查看 Request Body' })
                  : t('aiCallLogs.drawer.noBody', { defaultValue: '尚无 Body' })}
            </button>
          )}
        </div>
        <div className="ai-call-detail-section-url">
          <strong>{item.method || 'POST'}</strong> {item.url || '-'}
        </div>
        {req && req.status === 'uploading' && (
          <div className="ai-call-detail-placeholder">
            {t('aiCallLogs.drawer.uploading', {
              defaultValue: '正在上传到 TOS，请稍后再试…',
            })}
          </div>
        )}
        {req && req.body !== undefined && (
          <pre className="ai-call-detail-body">{formatBody(req.body, emptyBodyLabel)}</pre>
        )}
      </div>

      <div className="ai-call-detail-section">
        <div className="ai-call-detail-section-header">
          <h4 className="ai-call-detail-section-title">
            {t('aiCallLogs.drawer.sections.response', { defaultValue: 'Response' })}
          </h4>
          {!resp && (
            <button
              className="btn btn-secondary"
              onClick={loadResp}
              disabled={loading === 'resp' || !item.hasResponseBody}
            >
              {loading === 'resp'
                ? loadingLabel
                : item.hasResponseBody
                  ? t('aiCallLogs.drawer.viewResponseBody', { defaultValue: '查看 Response Body' })
                  : t('aiCallLogs.drawer.noBody', { defaultValue: '尚无 Body' })}
            </button>
          )}
        </div>
        {resp && resp.status === 'uploading' && (
          <div className="ai-call-detail-placeholder">
            {t('aiCallLogs.drawer.uploading', {
              defaultValue: '正在上传到 TOS，请稍后再试…',
            })}
          </div>
        )}
        {resp && resp.body !== undefined && (
          <pre className="ai-call-detail-body">{formatBody(resp.body, emptyBodyLabel)}</pre>
        )}
      </div>
    </Modal>
  );
}
